import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  ButtonGroup,
  DialogBody,
  Divider,
  Flex,
  Form,
  Heading,
  Page,
  PageContent,
  Toast,
  useToast,
} from '@zillow/constellation';
import { useGetSharedApplication } from '../../utils/apis/useGetSharedApplication';
import { useParams } from 'react-router-dom';
import { FullHeightLoadingMask, ModalWithFullHeightBody, TallHeightLoadingMask } from '../../utils/styled-components';
import { ConfirmPropertyAddress, EditPropertyAddress, ZipMismatch } from '../../components/ShareRequest';
import { useGetPropertyDetails } from '../../utils/apis/useGetPropertyDetails';
import { getAddressString, getAddressStringWithUnit } from '../../utils/shareApplication.utils';
import { RENTAL_MANAGER_WEB } from '../../constants/paths';
import useCreateProperty from '../../utils/apis/useCreateProperty';
import { useGetListingExistsWithRetry } from '../../utils/apis/useGetListingExistsWithRetry';
import { useAcceptApplicationShare } from '../../utils/apis/useAcceptApplicationShare';
import useCreateCappsUser from '../../utils/apis/useCreateCappsUser';
import { getLandlordReviewPath } from '../../constants/urls';
import { FormProvider, useForm } from 'react-hook-form';
import { useDeclineApplicationShare } from '../../utils/apis/useDeclineApplicationShare';
import type { ShareApplicationPropertyAddress } from '../../models';
import { logError } from '@zg-rentals/log-error';
import useGetProperties from '../../utils/apis/useGetProperties';
import { getListingFromAddressString } from '../../utils/address.utils';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import {
  emitShareRequestConfirmEvent,
  emitShareRequestEditEvent,
  emitShareRequestViewEvent,
} from '../../../client/analytics/clickstreamEvents';

const monitor = getGlobalMonitor();

type RouteProps = {
  shareId: string;
};

type EditFormData = {
  addressString: string;
  updatedAddress?: ShareApplicationPropertyAddress;
  propertyTypeCode: number;
  unit?: string;
  listingAlias?: string;
  message?: string;
};

export function ShareRequestPage() {
  const { shareId } = useParams<RouteProps>();
  const { data, isFetching, error: getShareError } = useGetSharedApplication(shareId);
  const propertyAddress = data?.data.response.propertyAddress;
  const applicationId = data?.data.response.applicationId;
  const listingAlias = data?.data.response.listingAlias;

  useEffect(() => {
    // If there is an application & listingAlias associated with the share, just redirect
    if (applicationId && listingAlias) {
      window.location.href = getLandlordReviewPath(listingAlias, applicationId);
    }
  }, [applicationId, listingAlias]);

  if (getShareError) {
    return (
      <Page backgroundColor="transparent">
        <PageContent>
          <Alert appearance="error" body="There was an error loading the application share, try again" />
        </PageContent>
      </Page>
    );
  }

  return !isFetching && propertyAddress && !listingAlias && !applicationId ? (
    <ShareRequestModal shareId={shareId} propertyAddress={propertyAddress} applicationId={applicationId} />
  ) : (
    <TallHeightLoadingMask />
  );
}

function ShareRequestModal({
  shareId,
  propertyAddress,
  applicationId,
}: {
  shareId: string;
  propertyAddress: ShareApplicationPropertyAddress;
  applicationId: string | null | undefined;
}) {
  const { enqueueToast } = useToast();
  const [listingAlias, setListingAlias] = useState('');
  const [isCreatingListing, setIsCreatingListing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isZipMismatch, setIsZipMismatch] = useState(false);
  const form = useForm<EditFormData>();
  const { data: propertyDetailsData, isFetching: isFetchingPropertyDetails } = useGetPropertyDetails(
    getAddressString(propertyAddress),
  );
  const { data: listingExists } = useGetListingExistsWithRetry(listingAlias);
  const propertyDetails = propertyDetailsData?.data.response;
  const createProperty = useCreateProperty({});
  const acceptApplicationShareMutation = useAcceptApplicationShare();
  const declineApplicationShareMutation = useDeclineApplicationShare();
  const { data: propertiesData, isFetching: isFetchingProperties } = useGetProperties({});

  const { mutate: createCappsUser } = useCreateCappsUser();
  useEffect(createCappsUser, [createCappsUser]);
  useEffect(() => {
    emitShareRequestViewEvent(applicationId);
  }, []);

  function onClose() {
    window.location.href = RENTAL_MANAGER_WEB;
  }

  function onClickEdit() {
    if (isZipMismatch) {
      setIsZipMismatch(false);
    } else {
      form.reset({
        propertyTypeCode: propertyDetails?.propertyTypeCode,
        unit: propertyAddress?.unit,
      });
      emitShareRequestEditEvent(applicationId);
      setIsEditing(!isEditing);
    }
  }

  function onClickEditConfirm(editData: EditFormData) {
    if (!isZipMismatch) {
      if (editData.updatedAddress) {
        if (propertyAddress?.zip === editData.updatedAddress?.zip) {
          monitor.count({ name: `ShareRequestPage onClickEditConfirm zipSame` });
          createListing(editData);
        } else {
          const addressWithUnit = getAddressStringWithUnit(editData.updatedAddress, editData.unit);
          form.setValue(
            'message',
            `Hello, the address you entered is incorrect. Can you update your application to include the corrected address, ${addressWithUnit}, and then resubmit your application? Thanks!`,
          );
          setIsZipMismatch(true);
        }
      }
    } else {
      monitor.count({ name: `ShareRequestPage onClickEditConfirm zipMismatch` });
      const { message, updatedAddress, unit } = editData;
      if (message && updatedAddress) {
        declineShare({ message, updatedAddress: { ...updatedAddress, unit } });
      }
    }
  }

  const editSubmitHandler = form.handleSubmit(onClickEditConfirm);

  async function onClickConfirm() {
    monitor.count({ name: `ShareRequestPage onClickConfirm` });
    emitShareRequestConfirmEvent(applicationId);
    if (!isEditing) {
      if (propertyDetails) {
        createListing({
          addressString: getAddressString(propertyAddress),
          propertyTypeCode: propertyDetails.propertyTypeCode || 6,
          unit: propertyAddress.unit,
        });
      }
    } else {
      editSubmitHandler();
    }
  }

  async function createListing(editedAddress: Partial<EditFormData>) {
    const previouslyEnteredListing = getListingFromAddressString({
      listings: propertiesData?.data.response?.listings,
      address: editedAddress.addressString,
    });
    setIsCreatingListing(true);
    if (!previouslyEnteredListing) {
      try {
        const response = await createProperty.mutateAsync({
          listingTypeCode: 1,
          fullAddress: editedAddress.addressString,
          listingAlias: null,
          propertyTypeCode: editedAddress.propertyTypeCode,
          unitNumber: editedAddress.unit,
        });
        setListingAlias(response.data.response.listingAlias);
        monitor.count({ name: `ShareRequestPage createListing newListing` });
      } catch (e) {
        if (e.response?.data?.error?.data?.duplicateListingAlias) {
          const { duplicateListingAlias } = e.response.data.error.data;
          setListingAlias(duplicateListingAlias);
          monitor.count({ name: `ShareRequestPage createListing duplicateListing` });
        } else if (e.response?.data?.error?.message === 'Unsupported propertyTypeCode') {
          enqueueToast(
            <Toast
              appearance="error"
              body="An application cannot be created for this address. Verify your address and try again."
            />,
          );
          monitor.count({ name: `ShareRequestPage createListing unsupportedProperty` });
          setIsCreatingListing(false);
        } else {
          logError({ context: 'ShareRequestPage createProperty', error: e });
          enqueueToast(<Toast appearance="error" body="An error has occurred. Verify your address and try again." />);
          setIsCreatingListing(false);
        }
      }
    } else {
      monitor.count({ name: `ShareRequestPage createListing duplicateListing` });
      setListingAlias(previouslyEnteredListing.listingAlias);
    }
  }

  async function acceptShare() {
    try {
      const response = await acceptApplicationShareMutation.mutateAsync({ listingAlias, shareId });
      window.location.href = getLandlordReviewPath(listingAlias, response.data.response.applicationId);
    } catch (e) {
      logError({ context: 'ShareRequestPage acceptApplicationShare', error: e });
      enqueueToast(<Toast appearance="error" body="An error has occurred. Verify your address and try again." />);
    }
  }

  async function declineShare({
    message,
    updatedAddress,
  }: {
    message: string;
    updatedAddress: ShareApplicationPropertyAddress;
  }) {
    try {
      await declineApplicationShareMutation.mutateAsync({ shareId, message, updatedAddress });
      window.location.href = RENTAL_MANAGER_WEB;
    } catch (e) {
      logError({ context: 'ShareRequestPage declineApplicationShare', error: e });
      enqueueToast(<Toast appearance="error" body="An error has occurred, try again" />);
    }
  }

  useEffect(() => {
    if (listingExists) {
      acceptShare();
    }
  }, [listingExists]);

  const isLoading =
    isFetchingPropertyDetails || isFetchingProperties || isCreatingListing || declineApplicationShareMutation.isPending;

  return (
    <ModalWithFullHeightBody
      isOpen
      fullScreen
      shouldCloseOnOutsideClick={false}
      renderHeader={(headerProps: object) => (
        <header {...headerProps}>
          <Flex
            display="flex"
            marginY="sm"
            marginX={{ default: 'xl', md_lte: 'sm' }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Flex
              display="flex"
              justifyContent="space-between"
              flexDirection={{ default: 'row', md_lte: 'column' }}
              alignItems="start"
              flexGrow={1}
            >
              <Heading level={5} as="h1">
                Zillow Rental Manager
              </Heading>
            </Flex>
            <Button buttonType="tertiary" onClick={onClose} marginLeft="md" type="button">
              Exit
            </Button>
          </Flex>
          <Divider />
        </header>
      )}
      renderBody={(bodyProps: object) => (
        <DialogBody {...bodyProps}>
          <FullHeightLoadingMask loading={isLoading}>
            {propertyAddress && propertyDetails && (
              <>
                {isEditing && (
                  <FormProvider {...form}>
                    <Form onSubmit={editSubmitHandler}>
                      {!isZipMismatch ? <EditPropertyAddress /> : <ZipMismatch />}
                    </Form>
                  </FormProvider>
                )}
                {!isEditing && (
                  <ConfirmPropertyAddress propertyAddress={propertyAddress} propertyDetails={propertyDetails} />
                )}
              </>
            )}
          </FullHeightLoadingMask>
        </DialogBody>
      )}
      renderFooter={(footerProps: object) => (
        <footer {...footerProps}>
          <Divider />
          <Flex
            as={ButtonGroup}
            display="flex"
            justifyContent="space-between"
            aria-label="Wizard actions"
            paddingY="sm"
            marginX={{ default: 'xl', md_lte: 'sm' }}
          >
            <Button onClick={onClickEdit} disabled={isLoading} type="button">
              {isEditing ? 'Back' : 'Edit address'}
            </Button>
            <Button onClick={onClickConfirm} buttonType="primary" disabled={isLoading}>
              {!isZipMismatch ? 'Confirm' : 'Send message'}
            </Button>
          </Flex>
        </footer>
      )}
      renderCloseButton={null}
    />
  );
}
